import {
  AUTO_FILL_LEVEL_FORFEITURE_MONTHS,
  formatAutoFillForfeitureDateTime,
} from "../config/autoFillLevelForfeitureConfig";
import { getLevelAmount } from "../config/autoFillProgramConfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
} from "@mui/material";

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, gap: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
      {value}
    </Typography>
  </Box>
);

const LevelWithdrawalSlot = ({
  slot,
  termsAccepted,
  submittingLevel,
  onRequestLevel,
}) => {
  const { level, unitAmount, earned, withdrawn, available, rules, canWithdraw, blockers, forfeitureWindow } =
    slot;
  const busy = submittingLevel === level;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Level {level} tranche · ₹{unitAmount.toLocaleString("en-IN")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Earned ₹{earned.toLocaleString("en-IN")} · Withdrawn ₹
            {withdrawn.toLocaleString("en-IN")} · Available ₹
            {available.toLocaleString("en-IN")}
          </Typography>
          {forfeitureWindow?.deadlineIso && (
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              Level reached:{" "}
              {formatAutoFillForfeitureDateTime(forfeitureWindow.reachedAt)} · Withdraw by:{" "}
              <strong>
                {formatAutoFillForfeitureDateTime(forfeitureWindow.deadlineIso)}
              </strong>
              {" "}
              ({AUTO_FILL_LEVEL_FORFEITURE_MONTHS} months). Unwithdrawn tracked: ₹
              {(forfeitureWindow.unwithdrawn || 0).toLocaleString("en-IN")} of ₹
              {getLevelAmount(level).toLocaleString("en-IN")} max.
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={!canWithdraw || !termsAccepted || busy}
          onClick={() => onRequestLevel(level, unitAmount)}
        >
          {busy ? "Submitting…" : `Withdraw ₹${unitAmount.toLocaleString("en-IN")}`}
        </Button>
      </Box>

      {rules?.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Requirements for withdrawal {level} (level {level} CO tranche):
          </Typography>
          <List dense disablePadding sx={{ pl: 1 }}>
            {rules.map((line, i) => (
              <ListItem key={i} disablePadding sx={{ py: 0 }}>
                <ListItemText
                  primary={`· ${line}`}
                  primaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {!canWithdraw && blockers?.length > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Not eligible yet
          </Typography>
          <List dense disablePadding>
            {blockers.map((item, index) => (
              <ListItem key={index} disablePadding sx={{ alignItems: "flex-start", py: 0.25 }}>
                <ListItemText
                  primary={item.reason}
                  secondary={item.action}
                  primaryTypographyProps={{ variant: "body2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {canWithdraw && !termsAccepted && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Accept the withdrawal terms below to enable this button.
        </Typography>
      )}
    </Paper>
  );
};

const AutoFillWithdrawalDialog = ({
  open,
  onClose,
  model,
  termsAccepted,
  onTermsAcceptedChange,
  onRequestLevel,
  submittingLevel,
  submitError = "",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Request withdrawal</DialogTitle>
      <DialogContent
        dividers
        sx={{
          userSelect: "text",
          WebkitUserSelect: "text",
          MozUserSelect: "text",
          cursor: "auto",
        }}
      >
        {!model ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {submitError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            ) : null}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              ₹{model.available.toLocaleString("en-IN")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {model.walletBreakdown?.programId ? (
                <>
                  Program <strong>{model.walletBreakdown.programId}</strong>
                  {model.walletBreakdown.isGenesisRoot
                    ? " (genesis parent)"
                    : " (selected AFP)"}
                  · level {model.programLevel}
                </>
              ) : (
                <>
                  Total you can withdraw · program level {model.programLevel}
                </>
              )}
            </Typography>
            {!model.walletBreakdown?.isGenesisRoot && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Amounts below are for this Program ID only (not the parent genesis wallet).
              </Typography>
            )}
            <Row
              label="Total earned"
              value={`₹${model.earned.toLocaleString("en-IN")}`}
            />
            <Row
              label="Already withdrawn"
              value={`₹${model.withdrawn.toLocaleString("en-IN")}`}
            />
            {(model.locked || 0) > 0 && (
              <Row
                label="Pending approval"
                value={`₹${model.locked.toLocaleString("en-IN")}`}
              />
            )}

            {model.walletBreakdown && (
              <Paper
                variant="outlined"
                sx={{ p: 2, mt: 2, mb: 1, bgcolor: "rgba(13, 148, 136, 0.04)" }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Where this balance comes from
                </Typography>
                <Row
                  label="Matrix CO (1st payment per level · autoFillPoints)"
                  value={`₹${model.walletBreakdown.matrixCoPoints.available.toLocaleString("en-IN")} available`}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, pl: 0.5 }}>
                  Earned ₹
                  {model.walletBreakdown.matrixCoPoints.earned.toLocaleString("en-IN")}{" "}
                  · matrix payment #1 per level (withdrawal tranche) for{" "}
                  {model.walletBreakdown.programId || "this program"}.
                </Typography>
                {model.walletBreakdown.isGenesisRoot &&
                  model.walletBreakdown.genesisAdminCo.available > 0 && (
                  <>
                    <Row
                      label="Genesis admin CO (program entry · autoFillCoPoints)"
                      value={`₹${model.walletBreakdown.genesisAdminCo.available.toLocaleString("en-IN")} available`}
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, pl: 0.5 }}>
                      Earned ₹
                      {model.walletBreakdown.genesisAdminCo.earned.toLocaleString(
                        "en-IN"
                      )}{" "}
                      · Company genesis ₹150 entry (separate from matrix tranche).
                    </Typography>
                  </>
                )}
                <Divider sx={{ my: 1 }} />
                <Row
                  label="Sum (used for withdrawal)"
                  value={`₹${model.walletBreakdown.totalAvailable.toLocaleString("en-IN")}`}
                />
                {model.walletBreakdown.programLedgerView.available <
                  model.walletBreakdown.totalAvailable - 0.01 && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    The program page may show ₹
                    {model.walletBreakdown.programLedgerView.available.toLocaleString(
                      "en-IN"
                    )}{" "}
                    until matrix POINTS ledger rows catch up; withdrawal uses the full
                    total above (matrix + genesis admin CO).
                  </Alert>
                )}
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Withdraw by level tranche
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Withdrawals 1–3: no extra conditions. Withdrawals 4–5: Ultra Plus required.
              Withdrawals 6–15: direct Ultra counts and team size (50% max per leg) apply.
              Complete each tranche in order after admin approval.
            </Typography>

            {model.levelSlots?.length === 0 ? (
              <Alert severity="info">
                No level tranches with CO Points to withdraw yet.
              </Alert>
            ) : (
              model.levelSlots.map((slot) => (
                <LevelWithdrawalSlot
                  key={slot.level}
                  slot={slot}
                  termsAccepted={termsAccepted}
                  submittingLevel={submittingLevel}
                  onRequestLevel={onRequestLevel}
                />
              ))
            )}

            <FormControlLabel
              sx={{ mt: 1, display: "flex", alignItems: "flex-start" }}
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => onTermsAcceptedChange(e.target.checked)}
                />
              }
              label="I agree to the withdrawal terms for requests from this page"
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoFillWithdrawalDialog;
